import React, { useState } from 'react'
import type { Placement } from '@floating-ui/react'
import * as FloatingUi from '@floating-ui/react'

export interface TooltipOptions {
  anchor?: React.ReactNode
  content?: React.ReactNode
  placement?: Placement
  customOffset?: { mainAxis?: number, crossAxis?: number } | number
  clickable?: boolean
  className?: string
}

// * https://floating-ui.com/docs/tooltip
const Tooltip: React.FC<TooltipOptions> = ({
  anchor,
  content,
  placement = 'top',
  customOffset = 10,
  clickable = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = React.useRef(null)

  const { x, y, refs, strategy, context } = FloatingUi.useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    // Make sure the tooltip stays on the screen
    whileElementsMounted: FloatingUi.autoUpdate,
    middleware: [
      FloatingUi.offset(customOffset),
      FloatingUi.flip({
        fallbackAxisSideDirection: 'start'
      }),
      FloatingUi.shift(),
      FloatingUi.arrow({ element: arrowRef })
    ]
  })

  // Event listeners to change the open state
  const hover = FloatingUi.useHover(context, { move: false, enabled: !clickable })
  const focus = FloatingUi.useFocus(context, { enabled: !clickable })
  const dismiss = FloatingUi.useDismiss(context)
  // Role props for screen readers
  const role = FloatingUi.useRole(context, { role: 'tooltip' })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = FloatingUi.useInteractions([
    hover,
    focus,
    dismiss,
    role
  ])

  return (
    <>
      <div onClick={clickable ? () => setIsOpen((v) => !v) : undefined} ref={refs.setReference} {...getReferenceProps()}>
        {anchor}
      </div>
      <FloatingUi.FloatingPortal>
        {isOpen && (
          <div
            className={`floating-ui-tooltip pro-closest-shadow ${className}`}
            ref={refs.setFloating}
            style={{
              // Positioning styles
              position: strategy,
              top: y ?? 0,
              left: x ?? 0
            }}
            {...getFloatingProps()}
          >
            {content}
            <FloatingUi.FloatingArrow ref={arrowRef} context={context} className='floating-ui-tooltip-arrow-svg' />
          </div>
        )}
      </FloatingUi.FloatingPortal>
    </>
  )
}

export default Tooltip
